import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Store, Phone, User, MapPin, Check, Loader2, ArrowLeft, Mic, MicOff, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import useSpeechToText from "@/hooks/useSpeechToText";

// Full-screen, rural-friendly Add Outlet page.
// Huge inputs, mandatory shop type (retailer / wholesaler), auto GPS capture,
// duplicate-phone handling, bilingual copy, voice input on the 3 free-text fields.
//
// Apr 2026 (Stage 2): when invoked from Direct Sale (?from=direct-sale), the page
// returns the newly created outlet via location.state so the sale flow auto-selects it.
export default function AddShop() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const fromDirectSale = new URLSearchParams(location.search).get("from") === "direct-sale";

  const [shopName, setShopName] = useState("");
  const [shopNameTa, setShopNameTa] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [shopType, setShopType] = useState(""); // "" | "retailer" | "wholesaler"
  const [geo, setGeo] = useState(null); // {lat, lng}
  const [geoState, setGeoState] = useState("idle"); // idle | loading | ok | err
  const [busy, setBusy] = useState(false);

  // Voice input — separate language toggle so user can dictate in TA while UI is EN (or vice-versa).
  const [voiceLang, setVoiceLang] = useState(lang === "ta" ? "ta-IN" : "en-IN");
  const [activeMic, setActiveMic] = useState(null); // 'name' | 'owner' | 'address' | null
  // Tracks how many times each field was filled by voice — controls replace-vs-append.
  const [voiceUsedCount, setVoiceUsedCount] = useState({ name: 0, owner: 0, address: 0 });
  const speech = useSpeechToText();

  // Tiny haptic helper — silently no-ops on desktop.
  const vibrate = (pattern) => {
    try { navigator.vibrate?.(pattern); } catch (_e) { /* ignore */ }
  };

  // Translate raw error codes from the speech hook into user-friendly toasts.
  React.useEffect(() => {
    if (!speech.error) return;
    const map = {
      not_supported: t("voice_not_supported"),
      permission_denied: t("mic_permission_denied"),
      no_speech: t("no_speech_detected"),
      error: t("voice_error"),
    };
    toast.error(map[speech.error] || t("voice_error"));
    setActiveMic(null);
    vibrate([30, 30, 30]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.error]);

  const startMic = (field, setter, currentValue) => {
    if (!speech.supported) { toast.error(t("voice_not_supported")); return; }
    if (speech.listening) {
      // Tap-again cancels.
      speech.stop();
      setActiveMic(null);
      vibrate([30, 30, 30]);
      return;
    }
    setActiveMic(field);
    vibrate(50); // start cue

    speech.start({
      lang: voiceLang,
      onResult: (text) => {
        // First voice input on this field → REPLACE. Subsequent voice inputs → APPEND.
        const isFirst = (voiceUsedCount[field] || 0) === 0;
        if (isFirst) {
          setter(text);
        } else {
          const prev = (currentValue || "").trim();
          setter(prev ? `${prev} ${text}` : text);
        }
        setVoiceUsedCount((c) => ({ ...c, [field]: (c[field] || 0) + 1 }));
        setActiveMic(null);
        vibrate([30, 30, 30]); // stop cue
      },
    });
  };

  // Hard safety: auto-stop the recognizer if it somehow keeps listening past 7 s.
  // (Chrome usually auto-ends on silence already; this is belt-and-braces.)
  React.useEffect(() => {
    if (!speech.listening) return;
    const t = setTimeout(() => { try { speech.stop(); } catch (_e) { /* ignore */ } setActiveMic(null); vibrate([30, 30, 30]); }, 7000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.listening]);

  // Try to grab GPS the moment the page opens; user can retry from a button too.
  React.useEffect(() => { captureGeo(); /* eslint-disable-next-line */ }, []);

  const captureGeo = () => {
    if (!navigator.geolocation) { setGeoState("err"); return; }
    setGeoState("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState("ok");
      },
      () => setGeoState("err"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const canSubmit =
    shopName.trim().length >= 2 &&
    phone.length === 10 &&
    (shopType === "retailer" || shopType === "wholesaler") &&
    !busy;

  const submit = async () => {
    if (!shopName.trim()) { toast.error(t("shop_name_required")); return; }
    if (phone.length !== 10) { toast.error(t("phone_invalid_10")); return; }
    if (!shopType) { toast.error(t("select_shop_type")); return; }

    setBusy(true);
    try {
      const res = await api.post("/field/add-shop", {
        shop_name: shopName.trim(),
        shop_name_ta: shopNameTa.trim() || null,
        owner_name: ownerName.trim() || null,
        phone,
        address: address.trim() || null,
        shop_type: shopType,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
      });
      toast.success(t("shop_saved"));
      const newShop = res.data?.shop;
      // If we came from Direct Sale, return there with the new outlet auto-selected.
      if (fromDirectSale && newShop) {
        const target = user?.role === "manager" ? "/manager/direct-sale" : "/salesman/direct-sale";
        nav(target, { replace: true, state: { newOutlet: newShop } });
        return;
      }
      // Otherwise route back to the actor's home.
      if (user?.role === "manager") nav("/manager");
      else if (user?.role === "salesman") nav("/salesman");
      else nav(-1);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(t("shop_exists"));
      } else if (status === 400) {
        const msg = typeof detail === "string" ? detail : t("shop_name_required");
        toast.error(msg);
      } else {
        toast.error(lang === "ta" ? "பிழை. மீண்டும் முயற்சி செய்" : "Error. Try again.");
      }
    } finally { setBusy(false); }
  };

  const inputCls =
    "w-full bg-[#F5F5F7] border border-transparent rounded-2xl px-5 py-5 text-lg font-semibold text-[#1D1D1F] placeholder:text-[#9CA3AF] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] outline-none transition";

  // Inline overlay rendered inside text inputs / textareas. Renders a clear (X)
  // pill when the field has text, plus the mic button. Mic shows a red "listening"
  // pulse when this field is the active recognizer; tap again to stop.
  const MicBtn = ({ field, setter, currentValue, testid, clearTestid, top = "top-1/2 -translate-y-1/2" }) => {
    if (!speech.supported) return null;
    const isActive = activeMic === field && speech.listening;
    const hasText = !!(currentValue || "").trim();
    const clearField = () => {
      setter("");
      // Clearing resets the "voice used" counter so the next dictation REPLACES instead of appending.
      setVoiceUsedCount((c) => ({ ...c, [field]: 0 }));
    };
    return (
      <div className={`absolute right-3 ${top} flex items-center gap-1.5`}>
        {hasText && (
          <button
            type="button"
            data-testid={clearTestid}
            onClick={clearField}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 grid place-items-center text-[#86868B] active:scale-95 transition"
            aria-label={lang === "ta" ? "அழி" : "Clear"}
            title={lang === "ta" ? "அழி" : "Clear"}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          data-testid={testid}
          onClick={() => startMic(field, setter, currentValue)}
          className={`w-11 h-11 rounded-xl grid place-items-center transition active:scale-95 ${
            isActive
              ? "bg-[#FF3B30] text-white animate-pulse shadow-lg shadow-red-500/30"
              : "bg-white border border-gray-200 text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
          }`}
          aria-label={t("voice_input")}
          title={t("voice_input")}
        >
          {isActive ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
      </div>
    );
  };

  // Helper text rendered under each voice-enabled field (only when API supported).
  const VoiceHint = ({ field }) =>
    speech.supported ? (
      <div className="text-[11px] text-[#86868B] mt-1.5 flex items-center gap-1">
        <span aria-hidden="true">🎤</span>
        <span>{activeMic === field && speech.listening ? t("listening") : t("voice_hint")}</span>
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-28">
      {/* Custom full-screen header — bigger back target, no language clutter */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-2 shadow-sm">
        <button
          data-testid="addshop-back-btn"
          onClick={() => nav(-1)}
          className="p-2.5 rounded-xl hover:bg-gray-100 active:scale-95 transition"
          aria-label="Back"
        >
          <ArrowLeft className="w-6 h-6 text-[#1D1D1F]" />
        </button>
        <div className="min-w-0">
          <div className="heading-font text-xl font-bold text-[#1D1D1F]" data-testid="addshop-title">
            {t("add_new_shop")}
          </div>
          <div className="text-xs text-[#86868B]">
            {lang === "ta" ? "விரைவா பதிவு செய்யலாம்" : "Quick onboarding"}
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto p-5 space-y-5 fade-up">
        {/* Voice language toggle — shown only when Web Speech API is available */}
        {speech.supported && (
          <div className="flex items-center justify-between bg-white rounded-2xl px-4 py-3 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[#0055FF]/10 grid place-items-center shrink-0">
                <Mic className="w-4 h-4 text-[#0055FF]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#86868B] font-bold">
                  {t("voice_language")}
                </div>
                <div className="text-xs text-[#86868B] truncate">
                  {speech.listening ? t("listening") : t("tap_mic_to_speak")}
                </div>
              </div>
            </div>
            <div className="inline-flex bg-[#F5F5F7] rounded-xl p-1 shrink-0">
              <button
                data-testid="voice-lang-en"
                onClick={() => setVoiceLang("en-IN")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  voiceLang === "en-IN" ? "bg-white text-[#0A1A3F] shadow-sm" : "text-[#86868B]"
                }`}
              >
                English
              </button>
              <button
                data-testid="voice-lang-ta"
                onClick={() => setVoiceLang("ta-IN")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  voiceLang === "ta-IN" ? "bg-white text-[#0A1A3F] shadow-sm" : "text-[#86868B]"
                }`}
              >
                தமிழ்
              </button>
            </div>
          </div>
        )}

        {/* Shop Type — first decision, big buttons */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2.5 block">
            {t("shop_type")} *
          </label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: "retailer", label: t("retailer") },
              { key: "wholesaler", label: t("wholesaler") },
            ].map((opt) => (
              <button
                key={opt.key}
                data-testid={`shop-type-${opt.key}`}
                onClick={() => setShopType(opt.key)}
                className={`rounded-2xl py-5 px-3 font-bold text-base border-2 transition active:scale-[0.98] flex flex-col items-center gap-1.5 ${
                  shopType === opt.key
                    ? "bg-[#0A1A3F] border-[#0A1A3F] text-white shadow-md"
                    : "bg-white border-gray-200 text-[#1D1D1F] hover:border-[#0055FF]/40"
                }`}
              >
                <Store className="w-6 h-6" />
                <span>{opt.label}</span>
                {shopType === opt.key && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </div>

        {/* Shop Name */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2 block">
            {t("shop_name")} *
          </label>
          <div className="relative">
            <input
              data-testid="addshop-name-input"
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder={t("enter_shop_name")}
              className={`${inputCls} ${speech.supported ? (shopName.trim() ? "pr-28" : "pr-16") : ""}`}
              autoFocus
            />
            <MicBtn field="name" setter={setShopName} currentValue={shopName} testid="addshop-name-mic" clearTestid="addshop-name-clear" />
          </div>
          <VoiceHint field="name" />
        </div>

        {/* Phone (login ID) */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2 block">
            {t("phone")} * <span className="text-[#0055FF] ml-1">({t("login_with_phone")})</span>
          </label>
          <div className="relative">
            <Phone className="w-5 h-5 text-[#86868B] absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="addshop-phone-input"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="9999999999"
              className={`${inputCls} pl-14 tracking-widest`}
            />
          </div>
        </div>

        {/* Owner Name — optional */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2 block">
            {t("owner_name")}
          </label>
          <div className="relative">
            <User className="w-5 h-5 text-[#86868B] absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="addshop-owner-input"
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder={lang === "ta" ? "உரிமையாளர் பெயர்" : "Owner full name"}
              className={`${inputCls} pl-14 ${speech.supported ? (ownerName.trim() ? "pr-28" : "pr-16") : ""}`}
            />
            <MicBtn field="owner" setter={setOwnerName} currentValue={ownerName} testid="addshop-owner-mic" clearTestid="addshop-owner-clear" />
          </div>
          <VoiceHint field="owner" />
        </div>

        {/* Address */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2 block">
            {t("shop_address")}
          </label>
          <div className="relative">
            <textarea
              data-testid="addshop-address-input"
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={lang === "ta" ? "தெரு, ஊர்..." : "Street, area, landmark…"}
              className={`${inputCls} resize-none ${speech.supported ? (address.trim() ? "pr-28" : "pr-16") : ""}`}
            />
            <MicBtn
              field="address"
              setter={setAddress}
              currentValue={address}
              testid="addshop-address-mic"
              clearTestid="addshop-address-clear"
              top="top-3"
            />
          </div>
          <VoiceHint field="address" />
        </div>

        {/* GPS status card */}
        <button
          data-testid="addshop-geo-btn"
          onClick={captureGeo}
          className={`w-full rounded-2xl px-5 py-4 flex items-center gap-3 border-2 transition text-left active:scale-[0.99] ${
            geoState === "ok"
              ? "bg-[#34C759]/10 border-[#34C759]/40"
              : geoState === "err"
              ? "bg-[#FF3B30]/10 border-[#FF3B30]/40"
              : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`w-11 h-11 rounded-xl grid place-items-center ${
              geoState === "ok" ? "bg-[#34C759]" : geoState === "err" ? "bg-[#FF3B30]" : "bg-[#0055FF]"
            }`}
          >
            {geoState === "loading" ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : geoState === "ok" ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <MapPin className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="heading-font text-sm font-bold text-[#1D1D1F]">
              {geoState === "ok"
                ? t("location_captured")
                : geoState === "loading"
                ? t("getting_location")
                : t("capture_location")}
            </div>
            {geoState === "ok" && geo && (
              <div className="text-xs text-[#86868B] truncate">
                {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}
              </div>
            )}
            {geoState === "err" && (
              <div className="text-xs text-[#FF3B30]">
                {lang === "ta" ? "தொடவும் — மீண்டும் முயற்சி" : "Tap to retry"}
              </div>
            )}
          </div>
        </button>

        {/* Tamil name (optional, collapsed by default) */}
        <details className="bg-white rounded-2xl px-5 py-4 border border-gray-100">
          <summary className="text-sm font-semibold text-[#0055FF] cursor-pointer select-none">
            + {t("shop_name_ta")}
          </summary>
          <input
            data-testid="addshop-name-ta-input"
            type="text"
            value={shopNameTa}
            onChange={(e) => setShopNameTa(e.target.value)}
            placeholder="கடை பெயர்"
            className={`${inputCls} mt-3`}
          />
        </details>
      </div>

      {/* Fixed Save CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-gray-100 px-5 py-4 z-20">
        <div className="max-w-xl mx-auto">
          <button
            data-testid="addshop-save-btn"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full bg-[#0A1A3F] hover:bg-[#0055FF] disabled:bg-gray-300 text-white rounded-2xl py-5 text-lg font-bold shadow-lg disabled:shadow-none transition active:scale-[0.99] flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> {t("logging_in")}
              </>
            ) : (
              <>
                <Check className="w-5 h-5" /> {t("save_shop")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
